import { CSSProperties } from 'react';

import { Column } from '@tanstack/react-table';
import { isBoolean, isEnumMember, isType } from 'utils/typeUtils';

import { WithSubData } from './DataDisplayTable';

export enum Alignment {
  left = 'left',
  center = 'center',
  right = 'right',
}

export type ColumnMeta = {
  alignment: Alignment;
  numeric: boolean;
  headerNoPill?: boolean;
  /**
   * Make the whole cell (the full <td>, including its padding) the row-select
   * toggle target — a Gmail-style generous hit area. Set on checkbox-only
   * selection columns; not for cells that also hold an interactive control
   * (e.g. an info-icon modal), which the cell-wide toggle would hijack.
   */
  fullCellSelect?: boolean;
  /** On a column group, styles the group's own header, not its columns'. */
  headerClassName?: string;
  cellClassName?: string;
};

export const isColumnMeta = isType<ColumnMeta>({
  numeric: isBoolean,
  alignment: isEnumMember(Alignment),
});

export const alignments = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export const getCommonPinningStyles = <T,>(
  column: Column<WithSubData<T>, unknown>,
  zIndex = 0,
  isHeader = false,
  isDragging = false,
): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned ? `${column.getStart('left')}px` : undefined,
    position: isPinned || isHeader ? 'sticky' : isDragging ? 'relative' : 'unset',
    width: column.getSize(),
    top: isHeader ? 0 : undefined,
    zIndex: isDragging
      ? zIndex + 4
      : isPinned && isHeader
        ? zIndex + 3
        : isHeader
          ? zIndex + 2
          : isPinned
            ? zIndex + 1
            : zIndex,
  };
};
